import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor – adds token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor – handles 401 and refreshes token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/token')
    ) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            { refresh_token: refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          );
          const { access_token, refresh_token: newRefreshToken } = response.data;
          localStorage.setItem('access_token', access_token);
          if (newRefreshToken) localStorage.setItem('refresh_token', newRefreshToken);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_info');
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => {
    // Backend expects 'username' field – we pass the email
    return api.post('/auth/token', { username: email, password });
  },
  register: (email, password, firstName, lastName) => {
    return api.post('/auth/register', {
      email,
      password,
      first_name: firstName || null,
      last_name: lastName || null,
    });
  },
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => {
    localStorage.clear();
  },
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
};

// Admin user management (real backend endpoints)
export const adminAPI = {
  // Get all users
  getUsers: async (page = 1, limit = 10, search = '') => {
    // Backend expects page and limit as query parameters
    const params = { page, limit };
    if (search) params.search = search; // for future backend search
    const response = await api.get('/users/all-users', { params });
    return response.data; // { page, limit, total, data }
  },
  // Deactivate user
  deactivateUser: async (userId) => {
    return api.patch(`/users/${userId}/disable`);
  },
  // Activate user
  activateUser: async (userId) => {
    return api.patch(`/users/${userId}/enable`);
  },
  // Update user data (first_name, last_name, email)
  updateUser: async (userId, userData) => {
    return api.post(`/users/edit-user-data/${userId}`, userData);
  },
  // Update user roles (send as array of role names)
  updateUserRoles: async (userId, roles) => {
    const response = await api.put(`/users/${userId}/roles`, roles, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response;
  },
  getUserRoles: async (userId) => {
    const response = await api.get(`/users/${userId}/roles`);
    // The backend returns an array of role objects: [{ id, name, ... }]
    // We extract the 'name' field.
    const roles = response.data.map(role => role.name);
    return { data: roles };
  },
};


export default api;